import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import { get, isEqual, uniqueId } from 'lodash'

import useUserPermission from 'lib/hooks/useUserPermission'
import { getRecalculateError, parseDataForSubmit } from 'utils/AssetHelper'
import { formatTime } from 'utils/FormatHelper'
import { checkAcquiredAtBelongToAssetCodeAutoGenerate } from 'components/organisms/assets/AssetForm/helper'
import { FORM_MODES } from 'models/form'
import { FEATURES_ID } from 'constants/const'
import { MASTER_PAGES } from 'constants/masterData'
import { selectUnitList } from 'store/settings/unit/selectors'
import { selectMasterData } from 'store/masterData/selectors'
import { selectTagList } from 'store/settings/tag/selectors'
import { selectAssetCodeAutoGenerateSettingListData } from 'store/settings/assetCode/selectors'
import { selectMonthlyClosingCurrent } from 'store/accounting/monthlyClosing/selectors'
import { cleanupUnit, fetchUnitsRequest } from 'store/settings/unit/actions'
import { cleanupTag, fetchTagList } from 'store/settings/tag/actions'
import { fetchAutoGenerateSettings } from 'store/settings/assetCode/actions'
import { fetchMonthlyClosingCurrent } from 'store/accounting/monthlyClosing/actions'
import { getExciseListSelector } from 'store/settings/excise/selectors'
import {
  selectAssetItemErrors,
  selectShowMemorandumValueErrorMessage,
  selectUploadPhotoData,
} from 'store/asset/selectors'
import { cleanupExcise, fetchExciseList } from 'store/settings/excise/actions'
import { ExciseListData } from 'models/excise'
import { validateDeletable } from '../AssetList/tableSettings/helper'
import * as recalculateApi from 'api/app/recalculate'

import { Button, HorizontalLine, LinkLabel } from 'components/atoms'
import ErrorComponent from './ErrorComponent'
import { BasicForm, BottomAction, ConfirmModal, DatePicker } from 'components/molecules'
import FormData from './FormData'
import { BottomMessage, SavedAtNotice, TopMessage, Wrapper } from './styles'
import useResource from 'lib/hooks/useResource'
import { showErrorsAppCall } from 'store/app/actions'
import { getTrimmedData, loadNs } from 'aa_common/front-end'

const t = loadNs(['components/organisms/assets/asset-form', 'common', 'components/molecules/basic-form'])

type Props = {
  errors?: any[]
  loading?: boolean
  initialAssetData: any
  tempAssetSavedAt?: any
  formMode?: FORM_MODES
  confirming?: boolean
  hasTerms?: boolean
  isTemporaryData?: boolean
  onSubmit: (values: any) => void
  onSaveTemp?: (key: string, data: any, extraKey?: string) => void
  onDelete?: (biid: string) => void
  showRequiredError?: boolean
  asset?: any
  isValidateAssetLoading?: boolean
}

const AssetForm: React.FC<Props> = ({
  errors,
  loading = false,
  initialAssetData,
  tempAssetSavedAt = null,
  formMode = FORM_MODES.NEW,
  confirming = false,
  hasTerms = true,
  isValidateAssetLoading = false,
  isTemporaryData = false,
  onSubmit,
  onSaveTemp,
  onDelete,
  showRequiredError,
  asset,
}) => {
  const dispatch = useDispatch()
  const [bottomSubmitId] = useState<string>(uniqueId('asset-form-bottom-submit'))
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)

  const exciseListSelector: ExciseListData = useSelector(getExciseListSelector, isEqual)
  const units: any[] = useSelector(selectUnitList, isEqual) || []
  const tags: any[] = useSelector(selectTagList, isEqual) || []
  const masterData: any = useSelector(selectMasterData, isEqual) || {}
  const assetCodeAutoGenerateSettingListData = useSelector(selectAssetCodeAutoGenerateSettingListData, isEqual)
  const currentMonthlyClosing = useSelector(selectMonthlyClosingCurrent, isEqual)
  const error = useSelector(selectAssetItemErrors)
  const uploading = useSelector(selectUploadPhotoData, isEqual)
  const showMemorandumValueErrorMessage = useSelector(selectShowMemorandumValueErrorMessage, isEqual)

  const [{ data: recalculateHistory }, getRecalculateHistory] = useResource(recalculateApi.getHistory)

  const buttonSubmitRef = useRef<any>(null)

  const isNewMode = formMode === FORM_MODES.NEW
  const isEditMode = formMode === FORM_MODES.EDIT
  const isImportMode = formMode === FORM_MODES.IMPORT

  const { permissions: fixedAssetPermissions } = useUserPermission(FEATURES_ID.FIXED_ASSETS)

  const currentSetting = useMemo(() => {
    const acquiredAt = get(asset, 'acquired_at')

    if (!assetCodeAutoGenerateSettingListData || !acquiredAt) return null
    const currentSettingValue = assetCodeAutoGenerateSettingListData.find(setting =>
      checkAcquiredAtBelongToAssetCodeAutoGenerate(setting, acquiredAt)
    )
    return currentSettingValue
  }, [assetCodeAutoGenerateSettingListData, asset])

  const handleSubmit = (values: any) => {
    const parsedValues = parseDataForSubmit(getTrimmedData(values))
    onSubmit(parsedValues)
  }

  const handleClickDelete = (): void => {
    setIsConfirmingDelete(true)
  }

  const handleCancelDelete = (): void => {
    setIsConfirmingDelete(false)
  }

  const handleDelete = (): void => {
    onDelete && onDelete(get(initialAssetData, 'biid'))
    setIsConfirmingDelete(false)
  }

  const renderTopMessage = () => <TopMessage>{t('msg_notice_for_change')}</TopMessage>

  const renderBottomMessage = () => {
    const deletable = validateDeletable(initialAssetData, currentMonthlyClosing)
    const disabled = !deletable || !fixedAssetPermissions.isDelete
    return (
      <BottomMessage>
        <span className={classNames({ disabled: disabled })}>
          <LinkLabel
            showTooltipMessage={!fixedAssetPermissions.isDelete ? t('actions.no_permission') : undefined}
            disabled={disabled}
            type="danger"
            onClick={handleClickDelete}
          >
            {t('delete_asset')}
          </LinkLabel>
          <label style={{ marginLeft: 10 }}>
            {deletable ? t('msg_notice_for_delete') : t('msg_notice_for_not_delete')}
          </label>
        </span>
      </BottomMessage>
    )
  }

  const renderBottomSubmitArea = () => {
    let containerId = undefined
    const isDisableRegisterButton =
      showMemorandumValueErrorMessage &&
      Object.keys(showMemorandumValueErrorMessage).some(key => showMemorandumValueErrorMessage[key])

    if (formMode === FORM_MODES.IMPORT) {
      containerId = 'asset-edit-import-form'
    }

    return (
      <BottomAction
        id={bottomSubmitId}
        style={{
          justifyContent: 'space-between',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
        }}
        containerId={containerId}
      >
        <div>
          {!confirming && tempAssetSavedAt && !isEditMode && (
            <SavedAtNotice>{t('msg_last_backup', { time: formatTime(tempAssetSavedAt) })}</SavedAtNotice>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {isEditMode && (
            <>
              <label>{t('edit_start_date')}</label>
              <DatePicker name="edited_at" allowClear={false} style={{ marginLeft: 10, width: 110 }} />
            </>
          )}
          <Button
            type="submit"
            size="custom"
            style={{ marginLeft: 24, width: 120, height: 42 }}
            disabled={!hasTerms || uploading || isDisableRegisterButton}
            btnRef={buttonSubmitRef}
          >
            {t('register')}
          </Button>
        </div>
      </BottomAction>
    )
  }

  const handleReloadMasterOptions = (pageName: string) => {
    switch (pageName) {
      case MASTER_PAGES.UNITS:
        dispatch(fetchUnitsRequest())
        break
    }
  }

  useEffect(() => {
    if (isEditMode || isNewMode || isImportMode) {
      getRecalculateHistory()
      const recalculateError = getRecalculateError(recalculateHistory)
      recalculateError && dispatch(showErrorsAppCall(recalculateError))
    }
  }, [isEditMode, isNewMode, isImportMode]) //eslint-disable-line

  useEffect(() => {
    const filters = {
      is_active: true,
    }

    if (isImportMode) {
      !exciseListSelector?.data?.length && dispatch(fetchExciseList({ filters }))
      units?.length === 0 && dispatch(fetchUnitsRequest())
      tags?.length === 0 && dispatch(fetchTagList({}))
      !assetCodeAutoGenerateSettingListData?.length && dispatch(fetchAutoGenerateSettings())
      !currentMonthlyClosing && dispatch(fetchMonthlyClosingCurrent())
    } else {
      dispatch(fetchExciseList({ filters }))
      dispatch(fetchUnitsRequest())
      dispatch(fetchTagList({}))
      dispatch(fetchAutoGenerateSettings())
      dispatch(fetchMonthlyClosingCurrent())
    }

    return function cleanup() {
      if (!isImportMode) {
        dispatch(cleanupExcise())
        dispatch(cleanupUnit())
        dispatch(cleanupTag())
      }
    }
  }, []) // eslint-disable-line

  return (
    <Wrapper>
      {isEditMode && renderTopMessage()}
      <ErrorComponent error={error} values={asset} currentSetting={currentSetting} />
      <BasicForm
        initialValues={initialAssetData}
        onSubmit={handleSubmit}
        errors={errors}
        loading={loading || isValidateAssetLoading}
        preventSubmitOnEnter={true}
      >
        <FormData
          initialValues={initialAssetData}
          units={units}
          tags={tags}
          masterData={masterData}
          formMode={formMode}
          isImportMode={isImportMode}
          buttonSubmit={buttonSubmitRef.current}
          isTemporaryData={isTemporaryData}
          onSaveTemp={onSaveTemp}
          onHandleReloadMasterOptions={handleReloadMasterOptions}
          showRequiredError={showRequiredError}
        />
        <HorizontalLine style={{ width: '100%', backgroundColor: '#D4D8DD' }} />
        {isEditMode && renderBottomMessage()}
        <div style={{ height: 30 }}></div>
        {renderBottomSubmitArea()}
      </BasicForm>
      <ConfirmModal
        message={t('messages.confirm_delete', { name: get(initialAssetData, 'name') })}
        visible={isConfirmingDelete}
        onOK={handleDelete}
        onCancel={handleCancelDelete}
      />
    </Wrapper>
  )
}

export default AssetForm
